/**
 * Cart line normalization — checkout-ready fields + legacy localStorage support.
 */

import { filterBlendAvailableHerbIds } from "./ingredients";
import { computeCartLinePrice } from "./pricing";

/** Names of herbs removed during last cart normalize (for one-time UI notice). */
let pendingCartHerbRemovals = [];

/** @returns {string[]} */
export function consumePendingCartHerbRemovals() {
  const names = [...pendingCartHerbRemovals];
  pendingCartHerbRemovals = [];
  return names;
}

export const CART_CURRENCY = "GBP";

/** @typedef {'gold'|'purple'} BaseType */
/** @typedef {'200ml'|'250ml'|'300ml'|'500ml'|'1L'} SizeId */
/** @typedef {'plain'|'custom'} ProductType */
/** @typedef {'jar'|'pouch'} PackagingType */

/**
 * @typedef {Object} CartItem
 * @property {string} signature
 * @property {string} blend_signature
 * @property {string} name
 * @property {string} [description]
 * @property {BaseType} base_type
 * @property {PackagingType} packaging_type
 * @property {number} size_ml
 * @property {string[]} herbs
 * @property {number} price
 * @property {number} qty
 * @property {ProductType} product_type
 * @property {boolean} is_custom
 * @property {string} sku
 * @property {number} unit_price
 * @property {string} currency
 */

const LEGACY_SHOWCASE_PREFIX = /^(.+)-default$/;

const SHOWCASE_ID_META = {
  "plain-pouch-250": { size_ml: 250, size_id: "250ml", product_type: "plain", base_type: "gold", packaging_type: "pouch" },
  "purple-plain-pouch-250": { size_ml: 250, size_id: "250ml", product_type: "plain", base_type: "purple", packaging_type: "pouch" },
  "trial-gold-200": { size_ml: 200, size_id: "200ml", product_type: "plain", base_type: "gold", packaging_type: "pouch" },
  "trial-purple-200": { size_ml: 200, size_id: "200ml", product_type: "plain", base_type: "purple", packaging_type: "pouch" },
  "plain-300": { size_ml: 300, size_id: "300ml", product_type: "plain", base_type: "gold", packaging_type: "jar" },
  "plain-500": { size_ml: 500, size_id: "500ml", product_type: "plain", base_type: "gold", packaging_type: "jar" },
  "plain-1L": { size_ml: 1000, size_id: "1L", product_type: "plain", base_type: "gold", packaging_type: "jar" },
  "custom-500": { size_ml: 500, size_id: "500ml", product_type: "custom", base_type: "gold", packaging_type: "jar" },
  "custom-1L": { size_ml: 1000, size_id: "1L", product_type: "custom", base_type: "gold", packaging_type: "jar" },
  "purple-plain-300": { size_ml: 300, size_id: "300ml", product_type: "plain", base_type: "purple", packaging_type: "jar" },
  "purple-plain-500": { size_ml: 500, size_id: "500ml", product_type: "plain", base_type: "purple", packaging_type: "jar" },
  "purple-plain-1L": { size_ml: 1000, size_id: "1L", product_type: "plain", base_type: "purple", packaging_type: "jar" },
  "purple-custom-500": { size_ml: 500, size_id: "500ml", product_type: "custom", base_type: "purple", packaging_type: "jar" },
  "purple-custom-1L": { size_ml: 1000, size_id: "1L", product_type: "custom", base_type: "purple", packaging_type: "jar" },
};

/**
 * @param {{ base_type: BaseType, packaging_type: PackagingType, size_ml: number, product_type: ProductType, herbs?: string[] }} p
 */
export function buildProductSku(p) {
  const b = p.base_type === "purple" ? "PUR" : "GLD";
  const pack = p.packaging_type === "pouch" ? "PCH" : "JAR";
  const ml = p.size_ml === 1000 ? 1000 : Number(p.size_ml || 500);
  const herbs = Array.isArray(p.herbs) ? p.herbs : [];
  const mode = p.product_type === "custom" || herbs.length > 0 ? "CST" : "PLN";
  return `MM-${b}-${pack}-${ml}-${mode}`;
}

/**
 * @param {Partial<CartItem> & Record<string, unknown>} shaped
 */
function enrichCartLine(shaped) {
  const ml = typeof shaped.size_ml === "number" && !Number.isNaN(shaped.size_ml) ? shaped.size_ml : 500;
  let packaging_type =
    shaped.packaging_type === "pouch" || shaped.packaging_type === "jar"
      ? /** @type {PackagingType} */ (shaped.packaging_type)
      : "jar";
  if (ml === 200 || ml === 250) packaging_type = "pouch";
  if (packaging_type !== "pouch" && packaging_type !== "jar") {
    packaging_type = ml === 200 || ml === 250 ? "pouch" : "jar";
  }
  let herbs = Array.isArray(shaped.herbs) ? shaped.herbs.map(String).filter(Boolean) : [];
  const { herbs: safeHerbs, removed } = filterBlendAvailableHerbIds(herbs);
  if (removed.length) {
    pendingCartHerbRemovals.push(...removed.filter((n) => !pendingCartHerbRemovals.includes(n)));
  }
  herbs = safeHerbs;
  let product_type = shaped.product_type === "custom" || shaped.product_type === "plain" ? shaped.product_type : "plain";
  if (ml === 200 || ml === 250) {
    herbs = [];
    product_type = "plain";
  }
  if (ml === 300) {
    herbs = [];
    product_type = "plain";
  }
  if (ml >= 500 && ml <= 1000 && herbs.length > 0) {
    product_type = "custom";
  }
  if (ml >= 500 && ml <= 1000 && herbs.length === 0 && product_type !== "custom") {
    product_type = "plain";
  }
  const is_custom = (product_type === "custom" || herbs.length > 0) && ml >= 500 && ml <= 1000;
  let blend_signature =
    typeof shaped.blend_signature === "string" && shaped.blend_signature.length > 0
      ? shaped.blend_signature
      : buildBlendSignature(
          shaped.base_type || "gold",
          mlToSizeId(ml),
          herbs,
          product_type,
          packaging_type,
        );
  if (removed.length > 0) {
    blend_signature = buildBlendSignature(
      shaped.base_type || "gold",
      mlToSizeId(ml),
      herbs,
      product_type,
      packaging_type,
    );
  }
  const line = {
    ...shaped,
    herbs,
    packaging_type,
    product_type,
    blend_signature,
    signature: removed.length > 0 ? blend_signature : shaped.signature || blend_signature,
  };
  line.price = computeCartLinePrice(line);
  const sku =
    typeof shaped.sku === "string" && shaped.sku.trim().length > 0
      ? shaped.sku.trim()
      : buildProductSku({
          base_type: line.base_type,
          packaging_type,
          size_ml: line.size_ml,
          product_type,
          herbs,
        });
  return {
    ...line,
    is_custom,
    sku,
    unit_price: line.price,
  };
}

/**
 * @param {SizeId|string} sizeId
 * @returns {number|null}
 */
export function sizeIdToMl(sizeId) {
  if (sizeId === "1L") return 1000;
  const m = String(sizeId).match(/^(\d+)ml$/);
  return m ? Number(m[1]) : null;
}

/**
 * @param {number} ml
 * @returns {SizeId}
 */
export function mlToSizeId(ml) {
  if (ml === 1000) return "1L";
  if (ml === 250) return "250ml";
  if (ml === 200) return "200ml";
  return /** @type {SizeId} */ (`${ml}ml`);
}

/**
 * Map ProductShowcase `size` labels to internal size ids.
 * @param {string} size
 * @returns {SizeId}
 */
export function showcaseProductSizeToSizeId(size) {
  if (!size) return "500ml";
  const s = String(size).trim();
  if (/^1\s*litre$/i.test(s) || /^1L$/i.test(s)) return "1L";
  const lead = s.match(/^(\d+)\s*ml\b/i);
  if (lead) return /** @type {SizeId} */ (`${lead[1]}ml`);
  if (/250/i.test(s) && /pouch|foil/i.test(s)) return "250ml";
  const m2 = s.match(/^(\d+)ml$/i);
  if (m2) return /** @type {SizeId} */ (`${m2[1]}ml`);
  return "500ml";
}

/**
 * @param {BaseType} baseType
 * @param {SizeId} sizeId
 * @param {string[]} herbs
 * @param {ProductType} productType
 * @param {PackagingType} [packaging]
 */
export function buildBlendSignature(baseType, sizeId, herbs, productType, packaging = "jar") {
  const sid = String(sizeId).trim();
  const ml = sizeIdToMl(sid);
  let pack = packaging === "pouch" ? "pouch" : "jar";
  if (ml === 200 || ml === 250) pack = "pouch";
  const h = (herbs || []).filter(Boolean);
  if (productType === "plain" && h.length === 0) {
    return `${baseType}-${sid}-${pack}-plain`;
  }
  if (h.length) {
    return `${baseType}-${sid}-${pack}-${h.join("-")}`;
  }
  return `${baseType}-${sid}-${pack}-custom`;
}

/**
 * Parse builder-style signatures (legacy without jar/pouch segment, or new with packaging).
 * @param {string} signature
 * @returns {{ base_type: BaseType, size_id: SizeId, herbs: string[], product_type: ProductType, packaging_type: PackagingType }|null}
 */
function parseBuilderStyleSignature(signature) {
  const parts = signature.split("-").filter(Boolean);
  if (parts.length < 3) return null;
  const base = parts[0];
  if (base !== "gold" && base !== "purple") return null;
  const sizeToken = parts[1];
  const validSizes = new Set(["200ml", "250ml", "300ml", "500ml", "1L"]);
  if (!validSizes.has(sizeToken)) return null;
  let idx = 2;
  /** @type {PackagingType} */
  let packaging_type = sizeToken === "200ml" || sizeToken === "250ml" ? "pouch" : "jar";
  if (parts[idx] === "jar" || parts[idx] === "pouch") {
    packaging_type = /** @type {PackagingType} */ (parts[idx]);
    idx += 1;
  }
  if (sizeToken === "200ml" || sizeToken === "250ml") {
    packaging_type = "pouch";
  }
  const rest = parts.slice(idx);
  if (rest.length === 1 && rest[0] === "plain") {
    return {
      base_type: /** @type {BaseType} */ (base),
      size_id: /** @type {SizeId} */ (sizeToken),
      herbs: [],
      product_type: "plain",
      packaging_type,
    };
  }
  if (rest.length === 1 && rest[0] === "custom") {
    return {
      base_type: /** @type {BaseType} */ (base),
      size_id: /** @type {SizeId} */ (sizeToken),
      herbs: [],
      product_type: "custom",
      packaging_type,
    };
  }
  return {
    base_type: /** @type {BaseType} */ (base),
    size_id: /** @type {SizeId} */ (sizeToken),
    herbs: rest,
    product_type: "custom",
    packaging_type,
  };
}

/**
 * @param {string} signature
 * @returns {{ base_type: BaseType, size_ml: number, herbs: string[], product_type: ProductType, blend_signature: string, packaging_type: PackagingType }|null}
 */
function inferFromLegacySignature(signature) {
  const parsed = parseBuilderStyleSignature(signature);
  if (parsed) {
    const size_ml = sizeIdToMl(parsed.size_id);
    if (size_ml == null) return null;
    let herbs = parsed.herbs.slice();
    let product_type = parsed.product_type;
    if (size_ml === 300 || size_ml === 250 || size_ml === 200) {
      herbs = [];
      product_type = "plain";
    }
    herbs = [...new Set(herbs.map(String).filter(Boolean))].slice(0, 3);
    const blend_signature = buildBlendSignature(
      parsed.base_type,
      parsed.size_id,
      herbs,
      product_type,
      parsed.packaging_type,
    );
    return {
      base_type: parsed.base_type,
      size_ml,
      herbs,
      product_type,
      blend_signature,
      packaging_type: parsed.packaging_type,
    };
  }

  const legacyShowcase = signature.match(LEGACY_SHOWCASE_PREFIX);
  if (legacyShowcase) {
    const id = legacyShowcase[1];
    const meta = SHOWCASE_ID_META[id];
    if (!meta) return null;
    const size_id = /** @type {SizeId} */ (meta.size_id);
    const base_type = /** @type {BaseType} */ (meta.base_type || "gold");
    const herbs = [];
    const defaultPack = meta.size_ml === 250 || meta.size_ml === 200 ? "pouch" : "jar";
    const blend_signature = buildBlendSignature(
      base_type,
      size_id,
      herbs,
      meta.product_type,
      /** @type {PackagingType} */ (meta.packaging_type || defaultPack),
    );
    return {
      base_type,
      size_ml: meta.size_ml,
      herbs,
      product_type: meta.product_type,
      blend_signature,
      packaging_type: /** @type {PackagingType} */ (meta.packaging_type || defaultPack),
    };
  }

  return null;
}

/**
 * @param {Partial<CartItem> & Record<string, unknown>} raw
 * @returns {CartItem}
 */
export function normalizeCartItem(raw) {
  const signature = String(raw.signature || "");
  const name = String(raw.name || "Sea Moss Gel");
  const description = raw.description != null ? String(raw.description) : "";
  const price = typeof raw.price === "number" && !Number.isNaN(raw.price) ? raw.price : 0;
  const qty = typeof raw.qty === "number" && raw.qty > 0 ? Math.floor(raw.qty) : 1;
  const currency = raw.currency === "GBP" ? "GBP" : CART_CURRENCY;

  const hasNewShape =
    (raw.base_type === "gold" || raw.base_type === "purple") &&
    typeof raw.size_ml === "number" &&
    !Number.isNaN(raw.size_ml) &&
    Array.isArray(raw.herbs) &&
    (raw.product_type === "plain" || raw.product_type === "custom") &&
    typeof raw.blend_signature === "string" &&
    raw.blend_signature.length > 0;

  if (hasNewShape) {
    const shaped = {
      signature,
      blend_signature: String(raw.blend_signature),
      name,
      description,
      base_type: /** @type {BaseType} */ (raw.base_type),
      packaging_type: raw.packaging_type === "pouch" || raw.packaging_type === "jar" ? raw.packaging_type : undefined,
      size_ml: raw.size_ml,
      herbs: raw.herbs.slice(),
      price,
      qty,
      product_type: /** @type {ProductType} */ (raw.product_type),
      currency,
      sku: typeof raw.sku === "string" ? raw.sku : undefined,
    };
    return enrichCartLine(shaped);
  }

  const inferred = inferFromLegacySignature(signature);
  if (inferred) {
    const product_type =
      raw.product_type === "plain" || raw.product_type === "custom"
        ? raw.product_type
        : inferred.product_type;
    const herbs =
      Array.isArray(raw.herbs) && raw.herbs.length ? raw.herbs.map(String) : inferred.herbs.slice();
    const blend_signature =
      typeof raw.blend_signature === "string" && raw.blend_signature.length > 0
        ? raw.blend_signature
        : inferred.blend_signature;

    const shaped = {
      signature,
      blend_signature,
      name,
      description,
      base_type: raw.base_type === "gold" || raw.base_type === "purple" ? raw.base_type : inferred.base_type,
      packaging_type:
        raw.packaging_type === "pouch" || raw.packaging_type === "jar" ? raw.packaging_type : inferred.packaging_type,
      size_ml: typeof raw.size_ml === "number" && !Number.isNaN(raw.size_ml) ? raw.size_ml : inferred.size_ml,
      herbs,
      price,
      qty,
      product_type,
      currency,
      sku: typeof raw.sku === "string" ? raw.sku : undefined,
    };
    return enrichCartLine(shaped);
  }

  const fallbackSize = 500;
  const fallbackSizeId = mlToSizeId(fallbackSize);
  const fallbackBase = /** @type {BaseType} */ ("gold");
  const blend_signature =
    typeof raw.blend_signature === "string" && raw.blend_signature.length > 0
      ? raw.blend_signature
      : buildBlendSignature(fallbackBase, fallbackSizeId, [], "plain", "jar");

  const shaped = {
    signature: signature || blend_signature,
    blend_signature,
    name,
    description,
    base_type: raw.base_type === "gold" || raw.base_type === "purple" ? raw.base_type : fallbackBase,
    packaging_type: raw.packaging_type === "pouch" || raw.packaging_type === "jar" ? raw.packaging_type : "jar",
    size_ml: typeof raw.size_ml === "number" && !Number.isNaN(raw.size_ml) ? raw.size_ml : fallbackSize,
    herbs: Array.isArray(raw.herbs) ? raw.herbs.map(String) : [],
    price,
    qty,
    product_type: raw.product_type === "custom" ? "custom" : "plain",
    currency,
    sku: typeof raw.sku === "string" ? raw.sku : undefined,
  };
  return enrichCartLine(shaped);
}

/**
 * @param {unknown} rawList
 * @returns {CartItem[]}
 */
export function normalizeCartItemsFromStorage(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map((row) => normalizeCartItem(/** @type {any} */ (row)));
}
